import React from 'react';
import PropTypes from 'prop-types';
import { useAddUserForm } from '../../hooks/useAdminForms';

// Add an existing Entra user to PricePath: search the directory, pick a real
// person, assign role + org + access — or create a new local account (invite).
// Logic: hooks/useAdminForms (shared with the redesign).
function AddUserModal({ orgs, me, onClose, onAdded }) {
    const {
        flatOrgs, query, setQuery, results, searching, searchError, noMatches, canCreate, picked, setPicked, pickNew,
        role, setRole, roleOptions, orgId, setOrgId, pickedOrg, perspective, pricepath, setPricepath, saving, error, save,
    } = useAddUserForm({ orgs, me, onAdded, onClose });

    return (
        <div className="ac-modal-backdrop" onClick={onClose}>
            <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ac-modal-head">
                    <h2>Add user from Entra</h2>
                    <button className="ac-modal-x" onClick={onClose} aria-label="Close">×</button>
                </div>

                {!picked ? (
                    <>
                        <p className="ac-modal-hint">Search your directory for someone who already has a sign-in — or type an email to create a new account.</p>
                        <input
                            className="ac-modal-search"
                            type="search"
                            autoFocus
                            placeholder="Search name or email…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <div className="ac-modal-results">
                            {searching && <div className="ac-muted">Searching Entra…</div>}
                            {!searching && searchError && <div className="ac-error" role="alert">{searchError}</div>}
                            {noMatches && (
                                <div className="ac-noresults">
                                    <div className="ac-muted">No matches in the directory.</div>
                                    {canCreate && (
                                        <button
                                            className="ac-invite-new"
                                            onClick={pickNew}
                                        >
                                            + Create <b>{query.trim()}</b> as a new account
                                        </button>
                                    )}
                                </div>
                            )}
                            {results.map(u => (
                                <button key={u.entra_object_id} className="ac-result" onClick={() => setPicked(u)}>
                                    <span className="ac-result-name">{u.display_name}</span>
                                    <span className="ac-result-email">{u.email}</span>
                                </button>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="ac-picked">
                            <div>
                                <div className="ac-result-name">{picked.invite ? picked.email : picked.display_name}</div>
                                {picked.invite
                                    ? <div className="ac-invite-badge">New — creates a local account with a temp password</div>
                                    : <div className="ac-result-email">{picked.email}</div>}
                            </div>
                            <button className="ac-picked-change" onClick={() => setPicked(null)}>Change</button>
                        </div>

                        <label className="ac-field">
                            <span>Role</span>
                            <select value={role} onChange={(e) => setRole(e.target.value)}>
                                {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </label>

                        <label className="ac-field">
                            <span>Organization</span>
                            <select value={orgId} onChange={(e) => setOrgId(e.target.value)} disabled={role === 'super_admin'}>
                                {flatOrgs.map(o => (
                                    <option key={o.id} value={o.id}>{' '.repeat(o.depth * 2)}{o.name}</option>
                                ))}
                            </select>
                        </label>
                        {pickedOrg && (
                            <p className="ac-modal-hint">
                                This decides what they see: <strong>{perspective}</strong>.
                            </p>
                        )}

                        <label className="ac-check">
                            <input type="checkbox" checked={pricepath} onChange={(e) => setPricepath(e.target.checked)} />
                            <span>Grant IronSight access</span>
                        </label>

                        {error && <div className="ac-error">{error}</div>}

                        <div className="ac-modal-actions">
                            <button className="ac-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
                            <button className="ac-btn-primary" onClick={save} disabled={saving || !orgId}>
                                {saving
                                    ? (picked.invite ? 'Creating…' : 'Adding…')
                                    : (picked.invite ? 'Create account' : 'Add user')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

AddUserModal.propTypes = {
    orgs: PropTypes.array.isRequired,
    me: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onAdded: PropTypes.func.isRequired,
};

export default AddUserModal;
